import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";

import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import {
	$agentInstructionFiles,
	agentInstructionPageId,
	isAgentInstructionPage,
} from "../document/agents-md.atom";
import {
	buildDefaultPagePath,
	buildFullSlugPath,
	findPageBySlugPath,
	generateUniqueSlug,
	sluggify,
} from "./page-metadata.utils";

export {
	buildDefaultPagePath,
	buildFullSlugPath,
	findPageBySlugPath,
	generateUniqueSlug,
	sluggify,
} from "./page-metadata.utils";

type PageMetadataBySlugPathParams = {
	/** The metadata key (from metadataKeyForDocsRoute) */
	metadataKey: string;
	/** The slug path segments from the URL */
	pageSlug: string;
};

/**
 * Look up a page by its fully-qualified slug path.
 *
 * The slug path is an array of segments representing the page hierarchy. For example, ["parent",
 * "child", "grandchild"] will find the page with slug "grandchild" whose parent has slug "child"
 * whose parent has slug "parent".
 */
export const $pageMetadataBySlugPath = atomFamily(
	({ metadataKey, pageSlug }: PageMetadataBySlugPathParams) => {
		const pageAtom = atom((get) => {
			const metadata = get($documentMetadata(metadataKey));
			if (!metadata) {
				return null;
			}

			const found = findPageBySlugPath(metadata.pagesMetadata, pageSlug);
			if (found) {
				return found;
			}

			// Fall back to agent instruction files (AGENTS.md, CLAUDE.md) which
			// live outside pagesMetadata but are navigable via the Misc section.
			if (!isAgentInstructionPage(pageSlug)) {
				return null;
			}

			const documentId = metadata.id;
			if (!documentId) {
				return null;
			}

			const agentFilesLoadable = get(loadable($agentInstructionFiles({ documentId })));

			if (agentFilesLoadable.state === "hasData") {
				// Filesystem check completed — return the match or null.
				const match = agentFilesLoadable.data.find((f) => f.pageMetadata.slug === pageSlug);
				return match?.pageMetadata ?? null;
			}

			// Still loading — return synthetic metadata so the page view
			// doesn't flash "not found" while the filesystem check runs.
			// Once the loadable resolves this atom re-evaluates.
			const pageId = agentInstructionPageId(pageSlug);
			return {
				id: pageId,
				slug: pageSlug,
				title: pageSlug,
				path: pageSlug,
				type: "markdown" as const,
				archived: false,
				parentPageID: null,
			};
		});
		pageAtom.debugLabel = `pageMetadataBySlugPath/${metadataKey}/${pageSlug}`;

		return pageAtom;
	},
	isEqual
);
